import React from 'react'
import { SelectedTrustBadge } from 'src/lib/types/merchantAttributes'
import { DragDropContext, Draggable, Droppable, DropResult, OnDragEndResponder } from 'react-beautiful-dnd'
import TrustBadge from './TrustBadge'
import { useCustomizeUIContext } from 'src/lib/contexts/CustomizeUIProvider'
import FormTile from 'src/components/common/containers/FormTile'
import { useLocale } from 'src/lib/hooks/useLocal'
import Permission from 'src/services/PermissionService'
import { permissionType } from 'src/lib/utils/constants'
import { reorderDraggableListItems } from 'src/lib/utils/helpers'

interface TrustBadgesEditorProps {
  canEdit: boolean
}

const TrustBadges: React.FC<TrustBadgesEditorProps> = ({ canEdit }) => {
  const { t } = useLocale()
  const trustBadgesConfig = useCustomizeUIContext(context => context.state.trustBadgesConfig)
  const setTrustBadgesConfig = useCustomizeUIContext(context => context.actions.setTrustBadgesConfigData)

  const activeBadges = trustBadgesConfig?.badges?.filter((badge: SelectedTrustBadge) => badge?.isActive) || []
  const inactiveBadges = trustBadgesConfig?.badges?.filter((badge: SelectedTrustBadge) => !badge?.isActive) || []

  const handleDragEnd: OnDragEndResponder = (result: DropResult) => {
    if (!result.destination) return
    if (result.destination.index === result.source.index) return

    const sourceIndex = result.source.index
    const destinationIndex = result.destination.index

    // Reorder the active badges array
    const reorderedActiveBadges = reorderDraggableListItems(
      activeBadges,
      sourceIndex,
      destinationIndex
    ) as SelectedTrustBadge[]

    // Update the badges array with the new order
    const updatedBadges = [...(trustBadgesConfig?.badges || [])]

    // Replace the active badges with the reordered ones
    let activeIndex = 0
    updatedBadges.forEach((badge, index) => {
      if (badge.isActive) {
        updatedBadges[index] = reorderedActiveBadges[activeIndex]
        activeIndex++
      }
    })

    setTrustBadgesConfig({ ...trustBadgesConfig, badges: updatedBadges })
  }

  return (
    <Permission permissionId={permissionType.TRUST_BADGES}>
      <FormTile title={t('trust_badges')} subtitle={t('select_four_badges')}>
        <>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId='active-badges' isDropDisabled={!canEdit}>
              {provided => (
                <ul
                  className={`${
                    Boolean(trustBadgesConfig?.showBadges) ? 'mb-1 border rounded-lg' : 'border-0'
                  } overflow-hidden`}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {activeBadges.map((badge: SelectedTrustBadge, index: number) => (
                    <Draggable draggableId={badge?.label} key={badge?.label} index={index} isDragDisabled={!canEdit}>
                      {provided => (
                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                          <TrustBadge
                            key={index}
                            badge={badge}
                            index={index}
                            isEnabled={Boolean(badge?.isActive)}
                            isInputDisabled={!canEdit}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>

          <ul className='mb-1 overflow-hidden border rounded-lg'>
            {inactiveBadges.map((badge: SelectedTrustBadge, index: number) => (
              <TrustBadge
                key={badge?.label}
                badge={badge}
                index={index}
                isEnabled={Boolean(badge?.isActive)}
                isInputDisabled={!canEdit}
              />
            ))}
          </ul>
        </>
      </FormTile>
    </Permission>
  )
}

export default TrustBadges
